import { type ReactNode } from "react";
import { TagIcon } from "@phosphor-icons/react/Tag";
import { BarChart } from "@freightsimple/ui-core/Display/BarChart";
import { Banner } from "@freightsimple/ui-core/Display/Banner";
import { Grid } from "@freightsimple/ui-core/PageLayout/Grid";
import { Page } from "@freightsimple/ui-core/PageLayout/Page";
import { Panel } from "@freightsimple/ui-core/PageLayout/Panel";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import { Timestamp } from "@freightsimple/ui-core/DateTime/Display/Timestamp";
import { asOffsetDateTime } from "@freightsimple/time/branded-types";
import { type CustomerSpecificPricingDetail } from "@freightsimple/generated-carriers-openapi-client/models/CustomerSpecificPricingDetail";
import { carriersApi } from "@/App/Auth/carrierApi";
import { formatMoney, formatPercent } from "@/App/Views/carrierFormatting";
import { CarrierLoadFailed } from "@/App/CarrierLoadFailed";
import { useCarrierFetch } from "@/App/useCarrierFetch";
import { CarrierPageLogo } from "@/App/CarrierPageLogo";

// Detail page for one customer-specific pricing agreement: where it stands and
// what's next, how the customer engages with the pricing (quotes / bookings /
// win rate / paid), a month-by-month chart, and a lifecycle timeline. The top
// nav returns to the list — no back button (we don't use that pattern).
export function CustomerSpecificPricingDetailView({
  agreementId,
}: {
  agreementId: string;
}) {
  const { state, refreshing, refresh } = useCarrierFetch(
    () =>
      carriersApi().getCarrierCustomerSpecificPricingAgreement({ agreementId }),
    agreementId,
  );
  const detail = state.status === "loaded" ? state.data : undefined;

  return (
    <Page
      leadingVisual={<CarrierPageLogo />}
      icon={TagIcon}
      title={detail?.agreement.companyName ?? "Customer Specific Pricing"}
      description={detail?.agreement.statusLabel}
      refreshing={refreshing}
    >
      {state.status === "error" && (
        <CarrierLoadFailed what="this agreement" onRetry={refresh} />
      )}
      {detail && <DetailBody_ detail={detail} />}
    </Page>
  );
}

function DetailBody_({ detail }: { detail: CustomerSpecificPricingDetail }) {
  const { agreement, stats, monthly, timeline } = detail;
  return (
    <Stack gap={4}>
      <Banner
        variant={agreement.needsCarrierAction ? "warning" : "info"}
        title="Next step"
      >
        {agreement.nextStep ?? "Nothing needed right now."}
      </Banner>

      <Panel title="Agreement">
        <Grid columns={2}>
          <DetailRow_ label="Account Name" value={agreement.accountName} />
          <DetailRow_ label="Account Number" value={agreement.accountNumber} />
          <DetailRow_ label="Service" value={agreement.serviceIdentifier} />
          <DetailRow_ label="Active Since" value={agreement.activatedAt} />
          <DetailRow_ label="Expires" value={agreement.expiresAt} />
        </Grid>
      </Panel>

      <Panel title="Engagement">
        <Stack gap={4}>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
            <StatCard_
              label="Quotes"
              value={(agreement.quoteCount ?? 0).toLocaleString()}
            />
            <StatCard_
              label="Booked"
              value={(agreement.bookingCount ?? 0).toLocaleString()}
            />
            <StatCard_ label="Win Rate" value={formatPercent(stats.winRate)} />
            <StatCard_
              label="Total Paid"
              value={formatMoney(stats.totalPaidCad)}
            />
            <StatCard_
              label="Avg / Shipment"
              value={formatMoney(stats.averagePaidPerShipmentCad)}
            />
          </div>
          <div className="flex flex-wrap gap-x-8 gap-y-1 text-sm text-content-secondary">
            <RecencyLine_ label="Last quoted" value={stats.lastQuotedAt} />
            <RecencyLine_ label="Last booked" value={stats.lastBookedAt} />
          </div>
        </Stack>
      </Panel>

      {/* Quotes and bookings are on very different scales, so one chart each. */}
      <Grid columns={2}>
        <Panel title="Quotes by Month">
          {monthly.length === 0 ? (
            <T size="sm" color="secondary">
              No quoting activity on this agreement yet.
            </T>
          ) : (
            <BarChart
              labels={monthly.map((m) => m.month)}
              series={[
                {
                  label: "Quoted",
                  color: "#cbd5e1",
                  values: monthly.map((m) => m.quoted ?? 0),
                },
              ]}
              formatValue={(v) => v.toString()}
            />
          )}
        </Panel>
        <Panel title="Bookings by Month">
          {monthly.length === 0 ? (
            <T size="sm" color="secondary">
              No bookings on this agreement yet.
            </T>
          ) : (
            <BarChart
              labels={monthly.map((m) => m.month)}
              series={[
                {
                  label: "Booked",
                  color: "#4c62ea",
                  values: monthly.map((m) => m.booked ?? 0),
                },
              ]}
              formatValue={(v) => v.toString()}
            />
          )}
        </Panel>
      </Grid>

      <Panel title="Timeline">
        {timeline.length === 0 ? (
          <T size="sm" color="secondary">
            No milestones yet.
          </T>
        ) : (
          <Stack gap={3}>
            {timeline.map((event) => (
              <div
                key={`${event.label}-${event.date ?? "pending"}`}
                className="flex items-start gap-3"
              >
                <div
                  className={
                    event.upcoming
                      ? "mt-1.5 h-2 w-2 shrink-0 rounded-full border border-foreground/30"
                      : "mt-1.5 h-2 w-2 shrink-0 rounded-full bg-nav-bg"
                  }
                />
                <div className="flex flex-col">
                  <span
                    className={
                      event.upcoming
                        ? "text-sm text-content-muted"
                        : "text-sm font-medium text-foreground"
                    }
                  >
                    {event.label}
                  </span>
                  {event.date && (
                    <span className="text-xs text-content-muted">
                      {event.date}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </Stack>
        )}
      </Panel>
    </Stack>
  );
}

function RecencyLine_({
  label,
  value,
}: {
  label: string;
  value: string | undefined;
}) {
  return (
    <span>
      <span className="text-content-muted">{label}: </span>
      {value ? (
        <Timestamp value={asOffsetDateTime(value)} relative />
      ) : (
        <span className="text-content-faint">never</span>
      )}
    </span>
  );
}

function StatCard_({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="text-xs tracking-wide text-content-muted uppercase">
        {label}
      </div>
      <div className="mt-1 text-lg font-bold text-foreground">{value}</div>
    </div>
  );
}

function DetailRow_({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <T size="xs" color="secondary">
        {label}
      </T>
      <T size="sm">{value ?? "—"}</T>
    </div>
  );
}
